// ============================================================================
// SHARED CONSTANTS
// ============================================================================

import { WeaponDefinition, WeaponId, HeroId, AbilityDefinition } from './types';

// ----------------------------------------------------------------------------
// COLORS (Cyberpunk Theme)
// ----------------------------------------------------------------------------

export const COLORS = {
  // Primary palette
  CYAN: '#0CE5F0',
  MAGENTA: '#F02BFF',
  YELLOW: '#FFE500',
  
  // UI colors
  BACKGROUND: '#0a0a12',
  PANEL: '#12121a',
  PANEL_BORDER: '#1a1a2e',
  
  // Team colors
  ATTACKER: '#FF4444',
  DEFENDER: '#44AAFF',
  
  // Health/Shield
  HEALTH: '#44FF44',
  SHIELD: '#44AAFF',
  DAMAGE: '#FF4444',
  
  // Text
  TEXT_PRIMARY: '#FFFFFF',
  TEXT_SECONDARY: '#AAAAAA',
  TEXT_ACCENT: '#0CE5F0',
} as const;

// ----------------------------------------------------------------------------
// WEAPON DEFINITIONS
// ----------------------------------------------------------------------------

export const WEAPONS: Record<WeaponId, WeaponDefinition> = {
  [WeaponId.BurstPistol]: {
    id: WeaponId.BurstPistol,
    name: 'Burst Pistol',
    price: 0,
    damage: 25,
    spread: 0.02,
    fireRate: 4,
    range: 800,
    ammoCapacity: 15,
    reloadTime: 1500,
    burstCount: 3,
    burstDelay: 60,
    projectileSpeed: 0,
    headshotMultiplier: 1.5,
  },
  [WeaponId.NeoSMG9]: {
    id: WeaponId.NeoSMG9,
    name: 'Neo SMG-9',
    price: 1100,
    damage: 18,
    spread: 0.06,
    fireRate: 12,
    range: 600,
    ammoCapacity: 30,
    reloadTime: 1800,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 1.3,
  },
  [WeaponId.PulseRifle]: {
    id: WeaponId.PulseRifle,
    name: 'Pulse Rifle',
    price: 1500,
    damage: 35,
    spread: 0.03,
    fireRate: 6,
    range: 1000,
    ammoCapacity: 25,
    reloadTime: 2000,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 2.0,
  },
  [WeaponId.ScatterShot]: {
    id: WeaponId.ScatterShot,
    name: 'Scatter Shot',
    price: 1400,
    damage: 15, // per pellet
    spread: 0.15,
    fireRate: 1.2,
    range: 300,
    ammoCapacity: 6,
    reloadTime: 2500,
    burstCount: 8, // 8 pellets
    burstDelay: 0, // all at once
    projectileSpeed: 0,
    headshotMultiplier: 1.5,
  },
  [WeaponId.RailSniper]: {
    id: WeaponId.RailSniper,
    name: 'Rail Sniper',
    price: 2100,
    damage: 120,
    spread: 0.005,
    fireRate: 0.8,
    range: 2000,
    ammoCapacity: 5,
    reloadTime: 3000,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 2.5,
  },
  [WeaponId.ShardLauncher]: {
    id: WeaponId.ShardLauncher,
    name: 'Shard Launcher',
    price: 600,
    damage: 40,
    spread: 0.01,
    fireRate: 3,
    range: 900,
    ammoCapacity: 12,
    reloadTime: 1800,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 1.8,
  },
  [WeaponId.NanoRepeater]: {
    id: WeaponId.NanoRepeater,
    name: 'Nano Repeater',
    price: 900,
    damage: 22,
    spread: 0.05,
    fireRate: 10,
    range: 750,
    ammoCapacity: 35,
    reloadTime: 2200,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 1.4,
  },
  [WeaponId.ArcWelder]: {
    id: WeaponId.ArcWelder,
    name: 'Arc Welder',
    price: 1300,
    damage: 20,
    spread: 0,
    fireRate: 15,
    range: 400,
    ammoCapacity: 100,
    reloadTime: 2500,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 1.2,
  },
  [WeaponId.PhaseRepeater]: {
    id: WeaponId.PhaseRepeater,
    name: 'Phase Repeater',
    price: 1800,
    damage: 28,
    spread: 0.025,
    fireRate: 7,
    range: 1200,
    ammoCapacity: 28,
    reloadTime: 2300,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 0,
    headshotMultiplier: 1.6,
  },
  [WeaponId.PlasmaCannon]: {
    id: WeaponId.PlasmaCannon,
    name: 'Plasma Cannon',
    price: 2800,
    damage: 80,
    spread: 0.01,
    fireRate: 1.5,
    range: 800,
    ammoCapacity: 8,
    reloadTime: 3500,
    burstCount: 1,
    burstDelay: 0,
    projectileSpeed: 600,
    headshotMultiplier: 1.0,
  },
};

// ----------------------------------------------------------------------------
// HERO DEFINITIONS
// ----------------------------------------------------------------------------

export interface HeroDefinition {
  id: HeroId;
  name: string;
  faction: string;
  role: string;
  health: number;
  shield: number;
  speed: number; // multiplier
  abilities: [AbilityDefinition, AbilityDefinition, AbilityDefinition, AbilityDefinition];
}

export const HEROES: Record<HeroId, HeroDefinition> = {
  [HeroId.Spectre]: {
    id: HeroId.Spectre,
    name: 'Spectre',
    faction: 'Vanta Syndicate',
    role: 'Flanker / Scout',
    health: 100,
    shield: 0,
    speed: 1.0,
    abilities: [
      { name: 'Ghost Protocol', description: '10% damage reduction', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Cloak', description: 'Invisible for 2s', cooldown: 10000, duration: 2000, charges: 1, chargeTime: 10000, isUltimate: false, ultimateCost: 0 },
      { name: 'Reveal Pulse', description: 'Reveal enemies nearby 3s', cooldown: 14000, duration: 3000, charges: 1, chargeTime: 14000, isUltimate: false, ultimateCost: 0 },
      { name: 'Shadow Dive', description: 'Teleport behind enemy + 75 dmg', cooldown: 50000, duration: 0, charges: 1, chargeTime: 50000, isUltimate: true, ultimateCost: 6 },
    ],
  },
  [HeroId.Byte]: {
    id: HeroId.Byte,
    name: 'Byte',
    faction: 'Collapse Unit',
    role: 'Control Specialist',
    health: 100,
    shield: 25,
    speed: 0.95,
    abilities: [
      { name: 'Quick Recovery', description: '+25% shield regen speed', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'EMP Pulse', description: 'AOE stun + slow 1.5s', cooldown: 12000, duration: 1500, charges: 1, chargeTime: 12000, isUltimate: false, ultimateCost: 0 },
      { name: 'Shock Trap', description: 'Slowing zone 5s', cooldown: 15000, duration: 5000, charges: 1, chargeTime: 15000, isUltimate: false, ultimateCost: 0 },
      { name: 'System Crash', description: 'Large AOE stun + slow', cooldown: 60000, duration: 3000, charges: 1, chargeTime: 60000, isUltimate: true, ultimateCost: 7 },
    ],
  },
  [HeroId.Vanta]: {
    id: HeroId.Vanta,
    name: 'Vanta',
    faction: 'HelixCorp',
    role: 'Tank / Frontline',
    health: 120,
    shield: 50,
    speed: 0.85,
    abilities: [
      { name: 'Titan Armor', description: '+50 shield, 15% damage reduction', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Energy Shield', description: 'Front barrier 3s, blocks bullets', cooldown: 10000, duration: 3000, charges: 1, chargeTime: 10000, isUltimate: false, ultimateCost: 0 },
      { name: 'Shockwave Slam', description: 'AOE stun + 30 dmg', cooldown: 14000, duration: 1000, charges: 1, chargeTime: 14000, isUltimate: false, ultimateCost: 0 },
      { name: 'Overclock', description: '40% DR + fire rate 5s', cooldown: 55000, duration: 4000, charges: 1, chargeTime: 55000, isUltimate: true, ultimateCost: 6 },
    ],
  },
  [HeroId.Katana]: {
    id: HeroId.Katana,
    name: 'Katana',
    faction: 'ArcLight Initiative',
    role: 'Burst Assassin',
    health: 90,
    shield: 0,
    speed: 1.15,
    abilities: [
      { name: 'Swift Strike', description: '+15% speed, +10% damage', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Blink', description: 'Teleport 3 tiles forward', cooldown: 8000, duration: 200, charges: 2, chargeTime: 8000, isUltimate: false, ultimateCost: 0 },
      { name: 'Invisibility', description: 'Cloak for 4 seconds', cooldown: 12000, duration: 4000, charges: 1, chargeTime: 12000, isUltimate: false, ultimateCost: 0 },
      { name: 'Shadow Strike', description: 'Teleport behind enemy + 75 dmg', cooldown: 50000, duration: 0, charges: 1, chargeTime: 50000, isUltimate: true, ultimateCost: 6 },
    ],
  },
  [HeroId.Glyph]: {
    id: HeroId.Glyph,
    name: 'Glyph',
    faction: 'ArcLight Initiative',
    role: 'Vision / Utility',
    health: 100,
    shield: 25,
    speed: 1.0,
    abilities: [
      { name: 'Keen Eyes', description: '+10% damage (precision)', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Poison Cloud', description: 'DOT zone 15dmg/tick 4s', cooldown: 10000, duration: 4000, charges: 1, chargeTime: 10000, isUltimate: false, ultimateCost: 0 },
      { name: 'Turret Deploy', description: 'Auto-attack turret 10s', cooldown: 14000, duration: 10000, charges: 1, chargeTime: 14000, isUltimate: false, ultimateCost: 0 },
      { name: 'Toxic Storm', description: 'Massive poison zone 5s', cooldown: 55000, duration: 5000, charges: 1, chargeTime: 55000, isUltimate: true, ultimateCost: 7 },
    ],
  },
  [HeroId.Spark]: {
    id: HeroId.Spark,
    name: 'Spark',
    faction: 'HelixCorp',
    role: 'Area Denial',
    health: 100,
    shield: 25,
    speed: 0.95,
    abilities: [
      { name: 'Fireproof', description: 'Fire immune, +10% fire rate', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Fire Bottle', description: 'Burning zone 15dmg/tick', cooldown: 12000, duration: 4000, charges: 2, chargeTime: 12000, isUltimate: false, ultimateCost: 0 },
      { name: 'Minigun Mode', description: '+50% fire rate 4s', cooldown: 15000, duration: 4000, charges: 1, chargeTime: 15000, isUltimate: false, ultimateCost: 0 },
      { name: 'Napalm Strike', description: 'Huge fire zone 6s', cooldown: 55000, duration: 6000, charges: 1, chargeTime: 55000, isUltimate: true, ultimateCost: 7 },
    ],
  },
  [HeroId.Zero]: {
    id: HeroId.Zero,
    name: 'Zero',
    faction: 'Collapse Unit',
    role: 'Sniper / Scout',
    health: 90,
    shield: 0,
    speed: 1.0,
    abilities: [
      { name: 'Steady Aim', description: '+15% damage (sniper)', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Mark Target', description: 'Reveal + 20% bonus dmg 5s', cooldown: 8000, duration: 5000, charges: 1, chargeTime: 8000, isUltimate: false, ultimateCost: 0 },
      { name: 'Dash Back', description: 'Quick backward escape', cooldown: 12000, duration: 200, charges: 1, chargeTime: 12000, isUltimate: false, ultimateCost: 0 },
      { name: 'Dome Shield', description: 'Team protection dome 3s', cooldown: 60000, duration: 3000, charges: 1, chargeTime: 60000, isUltimate: true, ultimateCost: 8 },
    ],
  },
  [HeroId.Mira]: {
    id: HeroId.Mira,
    name: 'Mira',
    faction: 'Independent',
    role: 'Medic / Support',
    health: 100,
    shield: 25,
    speed: 1.0,
    abilities: [
      { name: 'Combat Medic', description: '+25% healing received/given', cooldown: 0, duration: 0, charges: 1, chargeTime: 0, isUltimate: false, ultimateCost: 0 },
      { name: 'Heal Field', description: 'Healing zone 10HP/tick 4s', cooldown: 10000, duration: 4000, charges: 1, chargeTime: 10000, isUltimate: false, ultimateCost: 0 },
      { name: 'Shield Burst', description: '+30 shields to allies', cooldown: 14000, duration: 3000, charges: 1, chargeTime: 14000, isUltimate: false, ultimateCost: 0 },
      { name: 'Resync', description: 'Revive ally at 50% HP', cooldown: 60000, duration: 0, charges: 1, chargeTime: 60000, isUltimate: true, ultimateCost: 8 },
    ],
  },
};

// ----------------------------------------------------------------------------
// NETWORK CONSTANTS
// ----------------------------------------------------------------------------

export const NETWORK = {
  // Signaling - Use localhost for development, replace for production
  SIGNALING_SERVER: 'ws://localhost:8080',
  
  // Timing
  TICK_RATE: 20,
  SNAPSHOT_RATE: 10,
  INPUT_RATE: 20,
  PING_INTERVAL: 1000,
  TIMEOUT_MS: 10000,
  
  // Buffers
  INPUT_BUFFER_SIZE: 64,
  SNAPSHOT_BUFFER_SIZE: 32,
  INTERPOLATION_DELAY: 100, // ms
} as const;

// ----------------------------------------------------------------------------
// GAMEPLAY CONSTANTS
// ----------------------------------------------------------------------------

export const GAMEPLAY = {
  // Round
  ROUND_TIME: 180000,
  BUY_TIME: 30000,
  ROUNDS_TO_WIN: 7,
  MAX_ROUNDS: 13,
  
  // Spike
  SPIKE_PLANT_TIME: 3000,
  SPIKE_DEFUSE_TIME: 5000,
  SPIKE_UPLOAD_TIME: 45000,
  
  // Economy
  STARTING_CREDITS: 800,
  KILL_REWARD: 300,
  ASSIST_REWARD: 100,
  ROUND_WIN_REWARD: 3000,
  ROUND_LOSS_REWARD: 1900,
  LOSS_STREAK_BONUS: 500, // per consecutive loss
  MAX_LOSS_STREAK: 4,
  
  // Combat
  HEADSHOT_ZONE_RATIO: 0.3, // top 30% of hitbox
  FRIENDLY_FIRE: false,
  
  // Movement
  BASE_SPEED: 200,
  ACCELERATION: 1500,
  FRICTION: 800,
} as const;
